import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const tooltipVariants = cva(
  'z-50 overflow-hidden rounded-lg px-3 py-2 text-sm shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  {
    variants: {
      variant: {
        default:
          'border border-slate-200/60 bg-white/95 text-slate-900 backdrop-blur-sm dark:border-slate-700/60 dark:bg-slate-900/95 dark:text-slate-100',
        professional:
          'border border-slate-200/80 bg-white/98 text-slate-900 shadow-xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-900/98 dark:text-slate-100',
        glass:
          'border border-white/20 bg-white/10 text-white backdrop-blur-xl shadow-2xl dark:border-slate-800/40 dark:bg-slate-900/20 dark:text-slate-100',
        dark: 'border border-slate-700/80 bg-slate-900/95 text-white backdrop-blur-sm shadow-xl dark:border-slate-600/80 dark:bg-slate-800/95',
        light:
          'border border-slate-200/80 bg-white/95 text-slate-900 backdrop-blur-sm shadow-xl dark:border-slate-700/80 dark:bg-slate-100/95 dark:text-slate-900',
      },
      color: {
        secondary: 'border bg-popover text-popover-foreground',
        primary: 'border border-primary bg-primary text-primary-foreground',
        warning: 'border border-warning bg-warning text-warning-foreground',
        info: 'border border-info bg-info text-info-foreground',
        success: 'border border-success bg-success text-success-foreground',
        destructive:
          'border border-destructive bg-destructive text-destructive-foreground',
      },
    },
    defaultVariants: {
      variant: 'professional',
    },
  },
);

interface TolTipProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipVariants> {
  variant?: 'default' | 'professional' | 'glass' | 'dark' | 'light';
  color?:
    | 'primary'
    | 'secondary'
    | 'warning'
    | 'info'
    | 'success'
    | 'destructive';
}

const TooltipProvider: React.FC<
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>
> = ({ delayDuration = 0, ...props }) => (
  <TooltipPrimitive.Provider {...props} delayDuration={delayDuration} />
);
TooltipProvider.displayName = TooltipPrimitive.Provider.displayName;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipArrow = TooltipPrimitive.Arrow;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TolTipProps
>(({ className, sideOffset = 4, variant, color, children, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(tooltipVariants({ variant, color }), className)}
    {...props}
  >
    {children}
    <TooltipArrow className="fill-current opacity-20" />
  </TooltipPrimitive.Content>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipArrow,
};
